import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

export type MessageKey =
  | 'headingName'
  | 'fontSizeName'
  | 'body'
  | 'textColor'
  | 'textHighlight'
  | 'tabTextColor'
  | 'tabHighlightColor'
  | 'editorToolbarColorPickerAriaLabel'
  | 'editorToolbarFontSizeAriaLabel'
  | 'editorToolbarBoldAriaLabel'
  | 'editorToolbarItalicAriaLabel'
  | 'editorToolbarUnderlineAriaLabel'
  | 'editorToolbarStrikethroughAriaLabel'
  | 'editorToolbarHyperlinkAriaLabel'
  | 'editorToolbarAlignmentAriaLabel'
  | 'editorToolbarLeftAlignmentAriaLabel'
  | 'editorToolbarCenterAlignmentAriaLabel'
  | 'editorToolbarRightAlignmentAriaLabel'
  | 'editorToolbarBulletListAriaLabel'
  | 'editorToolbarNumberListAriaLabel'
  | 'gray10'
  | 'gray100'
  | 'grayWhite'
  | 'lightBlue100'
  | 'greenLight100'
  | 'orange100'
  | 'grimace100'
  | 'watermelonRed100'
  | 'yellorange100'
  | 'teal'
  | 'pink'
  | 'darkBlue'
  | 'purpleRain100'
  | 'yellow20'
  | 'greenLight20'
  | 'purpleRain20'
  | 'orange20'
  | 'lightBlue20'
  | 'watermelonRed20';

const messages: Record<MessageKey, MessageDescriptor> = defineMessages({
  headingName: {
    id: 'boxNewFormBuilder.richTextEditor.heading',
    description:
      'rich text heading style name where num is the heading number value',
    defaultMessage: 'Heading {num}',
  },
  fontSizeName: {
    id: 'boxNewFormBuilder.richTextEditor.fontSize',
    description:
      'rich text fontsize style name, where num is the font size number value',
    defaultMessage: '{num}pt',
  },
  body: {
    id: 'boxNewFormBuilder.richTextEditor.body',
    description: 'rich text body style name',
    defaultMessage: 'Body',
  },
  textColor: {
    id: 'boxNewFormBuilder.richTextEditor.textColor',
    description: 'Label for editor text color',
    defaultMessage: 'TEXT',
  },
  textHighlight: {
    id: 'boxNewFormBuilder.richTextEditor.textHighlight',
    description: 'Label for text editor highlight',
    defaultMessage: 'HIGHLIGHT',
  },
  tabTextColor: {
    id: 'boxNewFormBuilder.richTextEditor.tabTextColor',
    description: 'Label for tab for selecting text color',
    defaultMessage: 'Text',
  },
  tabHighlightColor: {
    id: 'boxNewFormBuilder.richTextEditor.tabHighlightColor',
    description: 'Label for tab for selecting text highlight color',
    defaultMessage: 'Highlight',
  },
  editorToolbarColorPickerAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarColorPickerAriaLabel',
    description: 'Aria label text for the color picker in the editor toolbar',
    defaultMessage: 'Color',
  },
  editorToolbarFontSizeAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarFontSizeAriaLabel',
    description:
      'Aria label text for the font size dropdown in the editor toolbar',
    defaultMessage: 'Font Size',
  },
  editorToolbarBoldAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarBoldAriaLabel',
    description: 'Aria label text for the bold button in the editor toolbar',
    defaultMessage: 'Bold',
  },
  editorToolbarItalicAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarItalicAriaLabel',
    description: 'Aria label text for the italic button in the editor toolbar',
    defaultMessage: 'Italic',
  },
  editorToolbarUnderlineAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarUnderlineAriaLabel',
    description:
      'Aria label text for the underline button in the editor toolbar',
    defaultMessage: 'Underline',
  },
  editorToolbarStrikethroughAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarStrikethroughAriaLabel',
    description:
      'Aria label text for the strikethrough button in the editor toolbar',
    defaultMessage: 'Strikethrough',
  },
  editorToolbarHyperlinkAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarHyperlinkAriaLabel',
    description:
      'Aria label text for the hyperlink button in the editor toolbar',
    defaultMessage: 'Hyperlink',
  },
  editorToolbarAlignmentAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarAlignmentAriaLabel',
    description:
      'Aria label text for the alignment dropdown in the editor toolbar',
    defaultMessage: 'Alignment',
  },
  editorToolbarLeftAlignmentAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarLeftAlignmentAriaLabel',
    description:
      'Aria label text for the left alignment button in the editor toolbar',
    defaultMessage: 'Left',
  },
  editorToolbarCenterAlignmentAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarCenterAlignmentAriaLabel',
    description:
      'Aria label text for the center alignment button in the editor toolbar',
    defaultMessage: 'Center',
  },
  editorToolbarRightAlignmentAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarRightAlignmentAriaLabel',
    description:
      'Aria label text for the right alignment button in the editor toolbar',
    defaultMessage: 'Right',
  },
  editorToolbarBulletListAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarBulletListAriaLabel',
    description:
      'Aria label text for the bullet list button in the editor toolbar',
    defaultMessage: 'Bullet List',
  },
  editorToolbarNumberListAriaLabel: {
    id: 'boxNewFormBuilder.richTextEditor.editorToolbarNumberListAriaLabel',
    description:
      'Aria label text for the number list button in the editor toolbar',
    defaultMessage: 'Number List',
  },
  gray10: {
    id: 'boxNewFormBuilder.richTextEditor.gray10',
    description: 'readable name of color token Gray10',
    defaultMessage: 'Gray',
  },
  gray100: {
    id: 'boxNewFormBuilder.richTextEditor.gray100',
    description: 'readable name of color token Gray100',
    defaultMessage: 'Black',
  },
  grayWhite: {
    id: 'boxNewFormBuilder.richTextEditor.grayWhite',
    description: 'readable name of color token GrayWhite',
    defaultMessage: 'White',
  },
  lightBlue100: {
    id: 'boxNewFormBuilder.richTextEditor.lightBlue100',
    description: 'readable name of color token LightBlue100',
    defaultMessage: 'Light Blue',
  },
  greenLight100: {
    id: 'boxNewFormBuilder.richTextEditor.greenLight100',
    description: 'readable name of color token GreenLight100',
    defaultMessage: 'Green',
  },
  orange100: {
    id: 'boxNewFormBuilder.richTextEditor.orange100',
    description: 'readable name of color token Orange100',
    defaultMessage: 'Orange',
  },
  grimace100: {
    id: 'boxNewFormBuilder.richTextEditor.grimace100',
    description: 'readable name of color token Grimace100 aka Indigo',
    defaultMessage: 'Indigo',
  },
  watermelonRed100: {
    id: 'boxNewFormBuilder.richTextEditor.watermelonRed100',
    description: 'readable name of color token WatermelonRed100',
    defaultMessage: 'Red',
  },
  yellorange100: {
    id: 'boxNewFormBuilder.richTextEditor.yellorange100',
    description: 'readable name of color token Yellorange100',
    defaultMessage: 'Yellow',
  },
  teal: {
    id: 'boxNewFormBuilder.richTextEditor.teal',
    description: 'readable name of color token Teal',
    defaultMessage: 'Teal',
  },
  pink: {
    id: 'boxNewFormBuilder.richTextEditor.pink',
    description: 'readable name of color token Pink',
    defaultMessage: 'Pink',
  },
  darkBlue: {
    id: 'boxNewFormBuilder.richTextEditor.darkBlue',
    description: 'readable name of color token DarkBlue',
    defaultMessage: 'Dark Blue',
  },
  purpleRain100: {
    id: 'boxNewFormBuilder.richTextEditor.purpleRain100',
    description: 'readable name of color token PurpleRain100',
    defaultMessage: 'Violet',
  },
  yellow20: {
    id: 'boxNewFormBuilder.richTextEditor.yellow20',
    description: 'readable name of color token Yellow20',
    defaultMessage: 'Yellow',
  },
  greenLight20: {
    id: 'boxNewFormBuilder.richTextEditor.greenLight20',
    description: 'readable name of color token GreenLight20',
    defaultMessage: 'Green',
  },
  purpleRain20: {
    id: 'boxNewFormBuilder.richTextEditor.purpleRain20',
    description: 'readable name of color token PurpleRain20',
    defaultMessage: 'Violet',
  },
  orange20: {
    id: 'boxNewFormBuilder.richTextEditor.orange20',
    description: 'readable name of color token Orange20',
    defaultMessage: 'Orange',
  },
  lightBlue20: {
    id: 'boxNewFormBuilder.richTextEditor.lightBlue20',
    description: 'readable name of color token LightBlue20',
    defaultMessage: 'Light Blue',
  },
  watermelonRed20: {
    id: 'boxNewFormBuilder.richTextEditor.watermelonRed20',
    description: 'readable name of color token WatermelonRed20',
    defaultMessage: 'Red',
  },
});

export default messages;
